import React, { useState, useEffect } from "react";
import { ApiRequest } from "@/entities/ApiRequest";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Send,
  Plus,
  X,
  Key,
  Lock,
  ChevronRight,
  AlertCircle,
  ArrowUpRight,
  Trash2,
  Save,
  Network
} from "lucide-react";

export default function ApiTester() {
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("");
  const [params, setParams] = useState([{ key: "", value: "" }]);
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [bodyType, setBodyType] = useState("none");
  const [rawBody, setRawBody] = useState("");
  const [formData, setFormData] = useState([{ key: "", value: "" }]);
  const [authType, setAuthType] = useState("none");
  const [authCredentials, setAuthCredentials] = useState({
    username: "",
    password: "",
    token: "",
    key: ""
  });
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedRequests, setSavedRequests] = useState([]);
  const [requestName, setRequestName] = useState("");
  const [editingName, setEditingName] = useState(null);
  const [showAllRequests, setShowAllRequests] = useState(false);

  useEffect(() => {
    loadSavedRequests();
  }, []);

  const loadSavedRequests = async () => {
    try {
      const requests = await ApiRequest.list("-created_date");
      setSavedRequests(requests);
    } catch (error) {
      console.error("Error loading saved requests:", error);
    }
  };

  const handleAddParam = (type) => {
    if (type === "params") {
      setParams([...params, { key: "", value: "" }]);
    } else if (type === "headers") {
      setHeaders([...headers, { key: "", value: "" }]);
    } else if (type === "formData") {
      setFormData([...formData, { key: "", value: "" }]);
    }
  };

  const handleRemoveParam = (index, type) => {
    if (type === "params") {
      setParams(params.filter((_, i) => i !== index));
    } else if (type === "headers") {
      setHeaders(headers.filter((_, i) => i !== index));
    } else if (type === "formData") {
      setFormData(formData.filter((_, i) => i !== index));
    }
  };

  const handleParamChange = (index, field, value, type) => {
    if (type === "params") {
      const newParams = [...params];
      newParams[index][field] = value;
      setParams(newParams);
    } else if (type === "headers") {
      const newHeaders = [...headers];
      newHeaders[index][field] = value;
      setHeaders(newHeaders);
    } else if (type === "formData") {
      const newFormData = [...formData];
      newFormData[index][field] = value;
      setFormData(newFormData);
    }
  };

  const buildRequestOptions = () => {
    const options = {
      method,
      headers: {}
    };

    // Add headers
    headers.forEach(({ key, value }) => {
      if (key && value) options.headers[key] = value;
    });

    // Add auth
    if (authType === "basic") {
      options.headers["Authorization"] = `Basic ${btoa(
        `${authCredentials.username}:${authCredentials.password}`
      )}`;
    } else if (authType === "bearer") {
      options.headers["Authorization"] = `Bearer ${authCredentials.token}`;
    } else if (authType === "api_key") {
      options.headers["X-API-Key"] = authCredentials.key;
    }

    // Add body
    if (method !== "GET" && method !== "HEAD") {
      if (bodyType === "raw" && rawBody) {
        try {
          options.body = JSON.stringify(JSON.parse(rawBody));
          options.headers["Content-Type"] = "application/json";
        } catch (e) {
          options.body = rawBody;
        }
      } else if (bodyType === "form-data") {
        const formDataObj = new FormData();
        formData.forEach(({ key, value }) => {
          if (key && value) formDataObj.append(key, value);
        });
        options.body = formDataObj;
      }
    }

    return options;
  };

  const saveRequest = async () => {
    if (!url) return;
    
    try {
      const requestData = {
        name: requestName || `${method} ${url.split("/")[2] || "Request"}`,
        method,
        url,
        params: params.filter(p => p.key && p.value),
        headers: headers.filter(h => h.key && h.value),
        bodyType,
        rawBody,
        formData: formData.filter(f => f.key && f.value),
        authType,
        authCredentials
      };

      const { id } = await ApiRequest.create(requestData);
      
      // Update the local state with the new request
      const newRequest = {
        ...requestData,
        id,
        created_date: new Date().toISOString()
      };
      
      setSavedRequests([newRequest, ...savedRequests]);
      setRequestName("");
    } catch (error) {
      console.error("Error saving request:", error);
    }
  };

  const loadRequest = (request) => {
    setMethod(request.method);
    setUrl(request.url);
    setParams(request.params.length > 0 ? request.params : [{ key: "", value: "" }]);
    setHeaders(request.headers.length > 0 ? request.headers : [{ key: "", value: "" }]);
    setBodyType(request.bodyType);
    setRawBody(request.rawBody);
    setFormData(request.formData.length > 0 ? request.formData : [{ key: "", value: "" }]);
    setAuthType(request.authType);
    setAuthCredentials(request.authCredentials);
  };

  const deleteRequest = async (id) => {
    try {
      await ApiRequest.delete(id);
      setSavedRequests(savedRequests.filter(req => req.id !== id));
    } catch (error) {
      console.error("Error deleting request:", error);
    }
  };

  const updateRequestName = async (id, newName) => {
    try {
      await ApiRequest.update(id, { name: newName });
      setSavedRequests(savedRequests.map(req => 
        req.id === id ? { ...req, name: newName } : req
      ));
      setEditingName(null);
    } catch (error) {
      console.error("Error updating request name:", error);
    }
  };

  const handleSend = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Build URL with query params
      let finalUrl = url;
      const queryParams = params
        .filter(({ key, value }) => key && value)
        .map(({ key, value }) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      
      if (queryParams) {
        finalUrl += `${url.includes("?") ? "&" : "?"}${queryParams}`;
      }

      const options = buildRequestOptions();
      const res = await fetch(finalUrl, options);
      let responseData;
      
      try {
        responseData = await res.json();
      } catch (e) {
        // If not JSON, get text
        responseData = await res.text();
      }
      
      const responseObj = {
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        data: responseData
      };
      
      setResponse(responseObj);
      
      // Automatically save the request if it was successful
      if (!requestName && !savedRequests.some(r => r.url === url && r.method === method)) {
        const autoName = `${method} ${url.split("/")[2] || "Request"}`;
        setRequestName(autoName);
        await saveRequest();
        loadSavedRequests(); // Reload to ensure we have the latest
      }
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">API Tester</h1>
        <div className="flex gap-2">
          <Input
            placeholder="Nome da requisição"
            value={requestName}
            onChange={(e) => setRequestName(e.target.value)}
            className="w-64"
          />
          <Button 
            onClick={saveRequest}
            variant="outline"
            className="gap-2"
            disabled={!url}
          >
            <Save className="w-4 h-4" />
            Salvar
          </Button>
        </div>
      </div>

      {savedRequests.length > 0 && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Histórico de Requisições</h2>
            {savedRequests.length > 3 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAllRequests(!showAllRequests)}
                className="gap-2"
              >
                {showAllRequests ? 'Mostrar menos' : 'Ver todas requisições'}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(showAllRequests ? savedRequests : savedRequests.slice(0, 3)).map((request) => (
              <Card
                key={request.id}
                className="p-4 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      request.method === 'GET' ? 'bg-blue-50 text-blue-600' :
                      request.method === 'POST' ? 'bg-green-50 text-green-600' :
                      request.method === 'PUT' ? 'bg-yellow-50 text-yellow-600' :
                      request.method === 'DELETE' ? 'bg-red-50 text-red-600' :
                      'bg-purple-50 text-purple-600'
                    }`}>
                      <Send className="w-5 h-5" />
                    </div>
                    <div>
                      {editingName === request.id ? (
                        <Input
                          value={request.name}
                          onChange={(e) => setSavedRequests(savedRequests.map(req =>
                            req.id === request.id ? { ...req, name: e.target.value } : req
                          ))}
                          onBlur={() => updateRequestName(request.id, request.name)}
                          onKeyDown={(e) => e.key === 'Enter' && updateRequestName(request.id, request.name)}
                          className="h-7 text-sm"
                          autoFocus
                        />
                      ) : (
                        <h3 
                          className="font-medium text-gray-900 cursor-pointer hover:text-gray-600"
                          onClick={() => setEditingName(request.id)}
                        >
                          {request.name}
                        </h3>
                      )}
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">
                        {request.url}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => loadRequest(request)}
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteRequest(request.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className={
                    request.method === 'GET' ? 'bg-blue-100 text-blue-800' :
                    request.method === 'POST' ? 'bg-green-100 text-green-800' :
                    request.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                    request.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                    'bg-purple-100 text-purple-800'
                  }>
                    {request.method}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {new Date(request.created_date).toLocaleString()}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Request Method and URL */}
      <Card className="p-4 mb-6">
        <div className="flex gap-2">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GET">GET</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Digite a URL da requisição"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button 
            onClick={handleSend}
            disabled={!url || loading}
            className="bg-sky-600 hover:bg-sky-700 min-w-[100px] text-white"
          >
            {loading ? "Enviando..." : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <Card className="p-4">
            <Tabs defaultValue="params">
              <TabsList className="w-full">
                <TabsTrigger value="params" className="flex-1">Params</TabsTrigger>
                <TabsTrigger value="auth" className="flex-1">Auth</TabsTrigger>
                <TabsTrigger value="headers" className="flex-1">Headers</TabsTrigger>
                <TabsTrigger value="body" className="flex-1">Body</TabsTrigger>
              </TabsList>

              <TabsContent value="params" className="space-y-4 mt-4">
                {params.map((param, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Key"
                      value={param.key}
                      onChange={(e) =>
                        handleParamChange(index, "key", e.target.value, "params")
                      }
                    />
                    <Input
                      placeholder="Value"
                      value={param.value}
                      onChange={(e) =>
                        handleParamChange(index, "value", e.target.value, "params")
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveParam(index, "params")}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => handleAddParam("params")}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Parâmetro
                </Button>
              </TabsContent>

              <TabsContent value="auth" className="space-y-4 mt-4">
                <Select value={authType} onValueChange={setAuthType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de autenticação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="api_key">API Key</SelectItem>
                  </SelectContent>
                </Select>

                {authType === "basic" && (
                  <div className="space-y-2">
                    <Input
                      placeholder="Username"
                      value={authCredentials.username}
                      onChange={(e) =>
                        setAuthCredentials({
                          ...authCredentials,
                          username: e.target.value
                        })
                      }
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={authCredentials.password}
                      onChange={(e) =>
                        setAuthCredentials({
                          ...authCredentials,
                          password: e.target.value
                        })
                      }
                    />
                  </div>
                )}

                {authType === "bearer" && (
                  <Input
                    placeholder="Token"
                    value={authCredentials.token}
                    onChange={(e) =>
                      setAuthCredentials({
                        ...authCredentials,
                        token: e.target.value
                      })
                    }
                  />
                )}

                {authType === "api_key" && (
                  <Input
                    placeholder="API Key"
                    value={authCredentials.key}
                    onChange={(e) =>
                      setAuthCredentials({
                        ...authCredentials,
                        key: e.target.value
                      })
                    }
                  />
                )}
              </TabsContent>

              <TabsContent value="headers" className="space-y-4 mt-4">
                {headers.map((header, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Key"
                      value={header.key}
                      onChange={(e) =>
                        handleParamChange(index, "key", e.target.value, "headers")
                      }
                    />
                    <Input
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) =>
                        handleParamChange(index, "value", e.target.value, "headers")
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveParam(index, "headers")}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => handleAddParam("headers")}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Header
                </Button>
              </TabsContent>

              <TabsContent value="body" className="space-y-4 mt-4">
                <Select value={bodyType} onValueChange={setBodyType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo do body" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    <SelectItem value="raw">Raw</SelectItem>
                    <SelectItem value="form-data">Form Data</SelectItem>
                  </SelectContent>
                </Select>

                {bodyType === "raw" && (
                  <Textarea
                    placeholder="Digite o body da requisição (JSON)"
                    value={rawBody}
                    onChange={(e) => setRawBody(e.target.value)}
                    className="font-mono"
                    rows={10}
                  />
                )}

                {bodyType === "form-data" && (
                  <div className="space-y-4">
                    {formData.map((item, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="Key"
                          value={item.key}
                          onChange={(e) =>
                            handleParamChange(
                              index,
                              "key",
                              e.target.value,
                              "formData"
                            )
                          }
                        />
                        <Input
                          placeholder="Value"
                          value={item.value}
                          onChange={(e) =>
                            handleParamChange(
                              index,
                              "value",
                              e.target.value,
                              "formData"
                            )
                          }
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveParam(index, "formData")}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={() => handleAddParam("formData")}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Campo
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        {/* Response Section */}
        <Card className="p-4">
          <h2 className="text-lg font-semibold mb-4">Resposta</h2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Erro na requisição</span>
              </div>
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {response && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="font-medium">Status:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  response.status >= 200 && response.status < 300
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {response.status} {response.statusText}
                </span>
              </div>

              <div>
                <h3 className="font-medium mb-2">Headers:</h3>
                <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(response.headers, null, 2)}
                </pre>
              </div>

              <div>
                <h3 className="font-medium mb-2">Body:</h3>
                <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                  {typeof response.data === 'object' 
                    ? JSON.stringify(response.data, null, 2)
                    : response.data}
                </pre>
              </div>
            </div>
          )}

          {!error && !response && (
            <div className="text-center text-gray-500 py-12">
              Faça uma requisição para ver a resposta
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}